<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
//#endregion

//#region types
interface Props {
  disabled?: boolean;
  label?: string;
  selectPlaceholder?: string;
  fieldList?: unknown[];
  permissionTypes?: string[];
  ids?: unknown[];
  mode?: string | null;
  showInheritCheckbox?: boolean;
  inherit?: unknown;
}

interface Emits {
  'update:inherit': [value: boolean];
  'update:ids': [value: unknown[]];
  'update:mode': [value: string | null];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  label: '',
  selectPlaceholder: '',
  fieldList: () => [],
  permissionTypes: () => ['allow_all', 'allow_selected', 'deny_selected'],
  ids: () => [],
  mode: null,
  showInheritCheckbox: false,
  inherit: () => false,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
//#endregion

//#region state
const internalActive = ref(false);
const internalType = ref<string | null>(null);
//#endregion

//#region computed
const permissionTypesList = computed(() => props.permissionTypes.map((item) => ({
  label: t(`permissions.${item}`),
  value: item,
})));
const permissionType = computed({
  get() {
    if (props.showInheritCheckbox && Boolean(props.inherit)) {
      return undefined;
    }
    return internalType.value || props.mode || undefined;
  },
  set(value: string | undefined) {
    internalType.value = value ?? null;
    emit('update:mode', value ?? null);

    if (value === 'allow_all') {
      emit('update:ids', []);
    }
  },
});
const selectedFields = computed(() => props.ids || []);
const isTagsInputDisabled = computed(() => (
  permissionType.value === 'allow_all'
  || props.disabled
  || (props.showInheritCheckbox && Boolean(props.inherit))
));
//#endregion

//#region methods
function isValidPermissionType(type: string | null) {
  return Boolean(type && props.permissionTypes.includes(type));
}

function handleFieldsUpdate(fields: unknown[]) {
  emit('update:ids', fields);
}

function handleInheritChange(value: boolean) {
  emit('update:inherit', value);
  if (!value && !permissionType.value) {
    const [firstType] = props.permissionTypes;
    permissionType.value = firstType;
  }
}
//#endregion

//#region watch
watch(() => props.mode, (newMode) => {
  if (isValidPermissionType(newMode)) {
    internalType.value = newMode;
    internalActive.value = true;
  } else {
    internalType.value = null;
    internalActive.value = false;
  }
}, { immediate: true });
//#endregion
</script>

<template>
  <div class="permission-row permission-tree">
    <div class="permission-tree__field">
      <span class="permission-tree__label">{{ props.label }}</span>
      <div
        v-if="props.showInheritCheckbox"
        class="inherit-check-wrapper"
      >
        <UiCheckbox
          :model-value="Boolean(props.inherit)"
          :disabled="props.disabled"
          class="permission-tree__checkbox"
          @update:model-value="handleInheritChange"
        />
      </div>
      <div class="permission-tree__branch">
        <div class="permission-controls">
          <UiRadioGroup
            v-model="permissionType"
            :disabled="props.disabled
              || (props.showInheritCheckbox && Boolean(props.inherit))"
            :options="permissionTypesList"
          >
            <UiSelectElement
              :model-value="selectedFields"
              :placeholder="props.selectPlaceholder"
              :list="props.fieldList"
              size="big"
              search-param="name"
              option-label-param="name"
              option-name-param="name"
              filterable
              multiple
              collapse-tag-tooltip
              :max-collapse-tags="1"
              :disabled="isTagsInputDisabled
                || (props.showInheritCheckbox && Boolean(props.inherit))"
              @update:model-value="handleFieldsUpdate"
            />
          </UiRadioGroup>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.el-select {
  width: 100%;
}

.permission-row {
  width: 100%;
}

.permission-tree {
  margin-top: 0;

  &__field {
    display: flex;
    flex-grow: 1;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    font-size: 14px;
    line-height: 20px;
  }

  &__checkbox {
    margin-right: 16px;
  }

  &__branch {
    position: relative;
    width: 100%;
    padding-left: 24px !important;
    padding-top: 8px;

    &::before,
    &::after {
      position: absolute;
      left: 7px;
      content: '';
      background: #eaeaea;
    }

    &::before {
      top: 0;
      width: 1px;
      height: 15px;
    }

    &::after {
      top: 14px;
      width: 12px;
      height: 1px;
    }
  }
}

.inherit-check-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  max-width: 65px;
  margin-right: 64px;
  line-height: 0;
}
.permission-controls {
  width: 100%;
}
</style>
